import {
  OverviewAlertSeverity,
  OverviewChartType,
  OverviewContext,
  OverviewGranularity,
  OverviewTableColumnType,
  OverviewValueFormat,
  OverviewVisibilityScope,
  type OverviewAlert,
  type OverviewCard,
  type OverviewChart,
  type OverviewChartSegment,
  type OverviewChartSeries,
  OverviewModule,
  type OverviewDateRangeMeta,
  type OverviewMeta,
  type OverviewResponse,
  type OverviewTable,
  type OverviewTableColumn,
} from "@ibada/shared-types/overview"

export type NormalizeOverviewOptions = {
  /** Used when meta.currency is missing */
  defaultCurrencyCode?: string | null
}

function normalizeString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null
  return normalizeString(value) ?? null
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string") {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

function normalizeNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null
  return normalizeNumber(value) ?? null
}

function coerceOverviewContext(value: unknown): OverviewContext {
  const s = normalizeString(typeof value === "string" ? value : undefined)
  // Global must be recognised, not folded into the Branch fallback: the meta drives what the UI claims the
  // numbers cover, so a consolidated response relabelled "branch" would describe the whole church as one branch.
  if (value === OverviewContext.Global || s === OverviewContext.Global) return OverviewContext.Global
  if (value === OverviewContext.Parish || s === OverviewContext.Parish) return OverviewContext.Parish
  return OverviewContext.Branch
}

function coerceOverviewGranularity(value: unknown): OverviewGranularity {
  const raw = typeof value === "string" ? value : String(value ?? "")
  const key = raw.trim().toLowerCase()
  switch (key) {
    case "daily":
      return OverviewGranularity.Daily
    case "weekly":
      return OverviewGranularity.Weekly
    case "monthly":
    default:
      return OverviewGranularity.Monthly
  }
}

function coerceOverviewValueFormat(value: unknown): OverviewValueFormat {
  const s = normalizeString(typeof value === "string" ? value : String(value ?? ""))?.toLowerCase()
  switch (s) {
    case "number":
      return OverviewValueFormat.Number
    case "money":
      return OverviewValueFormat.Money
    case "percentage":
      return OverviewValueFormat.Percentage
    case "date":
      return OverviewValueFormat.Date
    case "text":
    default:
      return OverviewValueFormat.Text
  }
}

function coerceOverviewChartType(value: unknown): OverviewChartType {
  const s = normalizeString(typeof value === "string" ? value : String(value ?? ""))
  if (
    value === OverviewChartType.HorizontalBar ||
    s === OverviewChartType.HorizontalBar ||
    s === "horizontal_bar"
  ) {
    return OverviewChartType.HorizontalBar
  }
  if (value === OverviewChartType.Bar || s === OverviewChartType.Bar) {
    return OverviewChartType.Bar
  }
  if (value === OverviewChartType.Donut || s === OverviewChartType.Donut) {
    return OverviewChartType.Donut
  }
  return OverviewChartType.Line
}

function coerceOverviewColumnType(value: unknown): OverviewTableColumnType {
  const s = normalizeString(typeof value === "string" ? value : String(value ?? ""))?.toLowerCase()
  switch (s) {
    case "number":
      return OverviewTableColumnType.Number
    case "money":
      return OverviewTableColumnType.Money
    case "date":
      return OverviewTableColumnType.Date
    case "badge":
      return OverviewTableColumnType.Badge
    case "text":
    default:
      return OverviewTableColumnType.Text
  }
}

function coerceOverviewAlertSeverity(value: unknown): OverviewAlertSeverity {
  const s = normalizeString(typeof value === "string" ? value : String(value ?? ""))?.toLowerCase()
  switch (s) {
    case "warning":
      return OverviewAlertSeverity.Warning
    case "danger":
      return OverviewAlertSeverity.Danger
    case "success":
      return OverviewAlertSeverity.Success
    case "info":
    default:
      return OverviewAlertSeverity.Info
  }
}

function coerceOverviewVisibilityScope(value: unknown): OverviewVisibilityScope | undefined {
  const s = normalizeString(typeof value === "string" ? value : undefined)?.toLowerCase()
  if (!s) return undefined

  switch (s) {
    case "branch_only":
      return OverviewVisibilityScope.BranchOnly
    case "parish_only":
      return OverviewVisibilityScope.ParishOnly
    case "both":
      return OverviewVisibilityScope.Both
    default:
      return undefined
  }
}

function coerceOverviewModule(value: unknown): OverviewModule {
  const s = normalizeString(typeof value === "string" ? value : String(value ?? ""))?.toLowerCase()
  switch (s) {
    case OverviewModule.Finance:
      return OverviewModule.Finance
    case OverviewModule.Members:
      return OverviewModule.Members
    case OverviewModule.Activities:
      return OverviewModule.Activities
    case OverviewModule.Assets:
      return OverviewModule.Assets
    case OverviewModule.Workspace:
      return OverviewModule.Workspace
    case OverviewModule.Dashboard:
      return OverviewModule.Dashboard
    default:
      return OverviewModule.Dashboard
  }
}

function normalizeOverviewDateRange(value: unknown): OverviewDateRangeMeta | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  return {
    from: normalizeNullableString(record.from ?? record.from_date),
    to: normalizeNullableString(record.to ?? record.to_date),
  }
}

function normalizeOverviewSeries(input: unknown): OverviewChartSeries {
  const record = (input ?? {}) as Record<string, unknown>
  const dataRaw = record.data ?? record.points
  const data = Array.isArray(dataRaw)
    ? dataRaw.map((point) => normalizeNumber(point) ?? 0)
    : []

  return {
    key: normalizeString(record.key) ?? "",
    labelKey: normalizeString(record.labelKey ?? record.label_key) ?? "",
    data,
  }
}

function normalizeOverviewSegments(input: unknown): OverviewChartSegment[] {
  if (!Array.isArray(input)) return []
  return input
    .map((item): OverviewChartSegment | null => {
      if (!item || typeof item !== "object") return null
      const record = item as Record<string, unknown>
      const value = normalizeNumber(record.value)
      if (value === undefined) return null
      return {
        labelKey: normalizeString(record.labelKey ?? record.label_key) ?? "",
        value,
        id: normalizeString(record.id),
      }
    })
    .filter((s): s is OverviewChartSegment => Boolean(s))
}

function normalizeOverviewChart(input: unknown): OverviewChart {
  const record = (input ?? {}) as Record<string, unknown>
  const labelsRaw = record.labels ?? record.label
  const labels = Array.isArray(labelsRaw)
    ? labelsRaw.map((label) => normalizeString(typeof label === "string" ? label : String(label ?? "")) ?? "")
    : []

  const seriesRaw = record.series ?? record.datasets
  const series = Array.isArray(seriesRaw) ? seriesRaw.map(normalizeOverviewSeries) : []

  const segmentsRaw = record.segments
  const segments = normalizeOverviewSegments(segmentsRaw)

  // Per-label shares in basis points, allocated by the backend so they sum to exactly 10000. A client that
  // derives its own percentages produces slices that do not add up — see OverviewChart.shares.
  const sharesRaw = record.shares
  const shares = Array.isArray(sharesRaw) ? sharesRaw.map((share) => normalizeNumber(share) ?? 0) : undefined

  const gRaw = record.granularity ?? record.chart_granularity
  const granularity =
    typeof gRaw === "string" && gRaw.trim().length > 0 ? gRaw.trim().toLowerCase() : undefined

  return {
    id: normalizeString(record.id) ?? "",
    titleKey: normalizeString(record.titleKey ?? record.title_key) ?? "",
    descriptionKey: normalizeNullableString(record.descriptionKey ?? record.description_key) ?? undefined,
    chartType: coerceOverviewChartType(record.chartType ?? record.chart_type),
    labels,
    series,
    granularity: granularity as OverviewGranularity | undefined,
    segments: segments.length > 0 ? segments : undefined,
    shares,
    visibilityScope: coerceOverviewVisibilityScope(record.visibilityScope ?? record.visibility_scope),
  }
}

function normalizeOverviewTableColumn(input: unknown): OverviewTableColumn {
  const record = (input ?? {}) as Record<string, unknown>
  return {
    key: normalizeString(record.key) ?? "",
    titleKey: normalizeString(record.titleKey ?? record.title_key) ?? "",
    columnType: coerceOverviewColumnType(record.columnType ?? record.column_type),
  }
}

function normalizeOverviewMeta(record: Record<string, unknown>, options: NormalizeOverviewOptions): OverviewMeta {
  const currencyFromApi = normalizeString(record.currency) ?? normalizeString(String(record.currency ?? ""))
  const fallback = normalizeString(options.defaultCurrencyCode ?? undefined) ?? null

  return {
    module: coerceOverviewModule(record.module),
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? "",
    context: coerceOverviewContext(record.context),
    branchId: normalizeNullableString(record.branchId ?? record.branch_id),
    parishBranchId: normalizeNullableString(record.parishBranchId ?? record.parish_branch_id),
    dateRange: normalizeOverviewDateRange(record.dateRange ?? record.date_range),
    granularity: coerceOverviewGranularity(record.granularity),
    currency: currencyFromApi || fallback || null,
    // Zero when absent, not "unknown": every board that excludes nothing reports nothing excluded, and a
    // nullable count would leave the UI unable to tell "complete" from "we never asked" (ADR-0024 §4).
    excludedBranchCount: Number(record.excludedBranchCount ?? record.excluded_branch_count ?? 0) || 0,
    scopeNoteKey: normalizeNullableString(record.scopeNoteKey ?? record.scope_note_key),
  }
}

function normalizeOverviewCard(input: unknown): OverviewCard {
  const record = (input ?? {}) as Record<string, unknown>
  const rawValue = record.value
  const value: unknown =
    rawValue === null || rawValue === undefined
      ? null
      : typeof rawValue === "number" || typeof rawValue === "string"
        ? rawValue
        : normalizeNumber(rawValue) ?? rawValue

  return {
    id: normalizeString(record.id) ?? "",
    titleKey: normalizeString(record.titleKey ?? record.title_key) ?? "",
    descriptionKey: normalizeNullableString(record.descriptionKey ?? record.description_key) ?? undefined,
    value,
    valueFormat: coerceOverviewValueFormat(record.valueFormat ?? record.value_format),
    visibilityScope: coerceOverviewVisibilityScope(record.visibilityScope ?? record.visibility_scope),
  }
}

function normalizeOverviewTable(input: unknown): OverviewTable {
  const record = (input ?? {}) as Record<string, unknown>
  const columnsRaw = record.columns
  const columns = Array.isArray(columnsRaw) ? columnsRaw.map(normalizeOverviewTableColumn) : []

  const rowsRaw = record.rows ?? record.data
  const rows: Record<string, unknown>[] = Array.isArray(rowsRaw)
    ? rowsRaw.map((row) => (row && typeof row === "object" ? (row as Record<string, unknown>) : {}))
    : []

  return {
    id: normalizeString(record.id) ?? "",
    titleKey: normalizeString(record.titleKey ?? record.title_key) ?? "",
    descriptionKey: normalizeNullableString(record.descriptionKey ?? record.description_key) ?? undefined,
    columns,
    rows,
    visibilityScope: coerceOverviewVisibilityScope(record.visibilityScope ?? record.visibility_scope),
  }
}

function normalizeOverviewAlert(input: unknown): OverviewAlert {
  const record = (input ?? {}) as Record<string, unknown>
  const actionRaw = record.action
  let action: OverviewAlert["action"] = undefined

  if (actionRaw && typeof actionRaw === "object") {
    const ar = actionRaw as Record<string, unknown>
    const titleKey = normalizeString(ar.titleKey ?? ar.title_key)
    const hrefRaw = normalizeNullableString(ar.href)
    if (titleKey) {
      action = { titleKey, href: hrefRaw ?? undefined }
    }
  }

  return {
    id: normalizeString(record.id) ?? "",
    titleKey: normalizeString(record.titleKey ?? record.title_key) ?? "",
    messageKey: normalizeNullableString(record.messageKey ?? record.message_key) ?? undefined,
    severity: coerceOverviewAlertSeverity(record.severity),
    count: normalizeNullableNumber(record.count) ?? undefined,
    action,
    visibilityScope: coerceOverviewVisibilityScope(record.visibilityScope ?? record.visibility_scope),
  }
}

/**
 * Parses any module overview GET response into OverviewResponse (defensive defaults on missing fields).
 */
export function normalizeOverviewResponse(input: unknown, options: NormalizeOverviewOptions = {}): OverviewResponse {
  const record = (input ?? {}) as Record<string, unknown>
  const metaRecord = record.meta && typeof record.meta === "object" ? (record.meta as Record<string, unknown>) : {}

  const cardsRaw = record.cards
  const chartsRaw = record.charts
  const tablesRaw = record.tables
  const alertsRaw = record.alerts

  const summary =
    record.summary && typeof record.summary === "object" ? (record.summary as Record<string, unknown>) : {}

  const cards = Array.isArray(cardsRaw) ? cardsRaw.map(normalizeOverviewCard) : []
  const charts = Array.isArray(chartsRaw) ? chartsRaw.map(normalizeOverviewChart) : []
  const tables = Array.isArray(tablesRaw) ? tablesRaw.map(normalizeOverviewTable) : []
  const alerts = Array.isArray(alertsRaw) ? alertsRaw.map(normalizeOverviewAlert) : []

  return {
    meta: normalizeOverviewMeta(metaRecord, options),
    summary,
    cards,
    charts,
    tables,
    alerts,
  }
}
